import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { BlameLogRefType, BlameLogType } from '@debbie/common/dist/blamelog/index.js';
import { isAdmin } from '@debbie/common/dist/permissions/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { blameLogs } from '@debbie/cortex/dist/api/index.js';
import * as creditorsAPI from '@debbie/cortex/dist/api/creditors.js';
import { selectRole } from '@debbie/cortex/dist/me/selectors.js';
import { selectTenant } from '@debbie/cortex/dist/tenant/selectors.js';
import keys from '@debbie/common/dist/cache/keys.js';
import rkiLogo from '../assets/images/rki-partner-logo.png';
import { Size } from '../design-system.js';
import OverrideSetting from './OverrideSetting.js';
import PlaceholderLoader from './PlaceholderLoader.js';
import { Checkbox, Well, Modal, Input, SmallHeader, Header } from './index.js';
import { creditorsEntity, useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import q from '@debbie/cortex/dist/query.js';
const CreditorExperianConfig = (props) => {
    const { id } = props;
    const lang = useSelector((s) => s.lang);
    const creditorLoadable = useCreditor(id);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const tenant = useSelector(selectTenant);
    const meRole = useSelector(selectRole);
    const [experianRequested, setExperianRequested] = useState(false);
    const [experianRequestLoading, setExperianRequestLoading] = useState(false);
    const [loadingExperianClientId, setLoadingExperianClientId] = useState(false);
    const [aksEnabled, setAksEnabled] = useState(false);
    const [experianClientId, setExperianClientId] = useState(null);
    const [autoDebtRegisterModelOpen, setAutoDebtRegisterModelOpen] = useState(false);
    const [state, setState] = useState({
        autoDebtRegister: creditor?.settings.autoDebtRegister !== undefined
            ? creditor?.settings.autoDebtRegister
            : tenant?.settings?.autoDebtRegister
                ? tenant.settings.autoDebtRegister
                : false,
        overrideAutoDebtRegister: false,
        autoRKIOpen: false,
        autoRKIIgnoreUnresolvedOppositions: false,
    });
    useEffect(() => {
        if (creditor && tenant) {
            setAksEnabled(creditor.aksEnabled);
            setExperianClientId(creditor.experianClientId || null);
            setState({
                ...state,
                overrideAutoDebtRegister: creditor.settings.overrideAutoDebtRegister || false,
                autoDebtRegister: creditor.settings.autoDebtRegister === undefined
                    ? !!tenant.settings.autoDebtRegister
                    : creditor.settings.autoDebtRegister,
                autoRKIOpen: creditor.settings.autoRKIOpen === undefined
                    ? !!tenant.settings.autoRKIOpen
                    : creditor.settings.autoRKIOpen || false,
                autoRKIIgnoreUnresolvedOppositions: creditor.settings?.autoRKIIgnoreUnresolvedOppositions === undefined
                    ? !!tenant.settings.autoRKIIgnoreUnresolvedOppositions
                    : creditor.settings.autoRKIIgnoreUnresolvedOppositions || false,
            });
        }
    }, [creditor, tenant]);
    const updateAutoDebtRegister = async (_creditor, autoDebtRegister) => {
        await creditorsAPI.update(id, {
            settings: {
                ..._creditor.settings,
                autoDebtRegister,
            },
        });
        q.invalidate(keys.write.modify(creditorsEntity, id));
    };
    const updateAutoRKIOpen = async (_creditor, autoRKIOpen) => {
        await creditorsAPI.update(id, {
            settings: {
                ..._creditor.settings,
                autoRKIOpen,
            },
        });
        q.invalidate(keys.write.modify(creditorsEntity, id));
    };
    const updateAutoRKIIgnoreUnresolvedOppositions = async (_creditor, autoRKIIgnoreUnresolvedOppositions) => {
        await creditorsAPI.update(id, {
            settings: {
                ..._creditor.settings,
                autoRKIIgnoreUnresolvedOppositions,
            },
        });
        q.invalidate(keys.write.modify(creditorsEntity, id));
    };
    const updateOverrideAutoDebtRegister = async (_creditor, overrideAutoDebtRegister, autoDebtRegister, autoRKIOpen, autoRKIIgnoreUnresolvedOppositions) => {
        if (overrideAutoDebtRegister) {
            await creditorsAPI.update(id, {
                settings: {
                    ..._creditor.settings,
                    overrideAutoDebtRegister,
                    autoDebtRegister,
                    autoRKIOpen,
                    autoRKIIgnoreUnresolvedOppositions,
                },
            });
        }
        else {
            await creditorsAPI.update(id, {
                settings: {
                    ..._creditor.settings,
                    overrideAutoDebtRegister,
                    autoDebtRegister: undefined,
                    autoRKIOpen: undefined,
                    autoRKIIgnoreUnresolvedOppositions: undefined,
                },
            });
        }
        q.invalidate(keys.write.modify(creditorsEntity, id));
    };
    return (React.createElement("div", null, creditor ? (React.createElement(React.Fragment, null,
        React.createElement("div", null,
            React.createElement(SmallHeader, null, lang.creditors.experianClientId),
            React.createElement(Input, { type: "number", button: experianClientId !== creditor.experianClientId
                    ? {
                        value: lang.creditors.updateExperianClientId,
                        loading: loadingExperianClientId,
                        onClick: async () => {
                            setLoadingExperianClientId(true);
                            try {
                                await creditorsAPI.update(creditor.creditorId, {
                                    experianClientId: experianClientId,
                                });
                                const blameLog = {
                                    details: { clientId: experianClientId },
                                    refId: creditor.creditorId,
                                    refType: BlameLogRefType.Creditor,
                                    type: BlameLogType.AKSClientID,
                                };
                                tenant &&
                                    (await blameLogs.createBlameLog(blameLog, tenant.id));
                                q.invalidate(keys.write.modify(creditorsEntity, id));
                            }
                            catch (error) { }
                            setExperianClientId(null);
                            setLoadingExperianClientId(false);
                        },
                    }
                    : !creditor.experianClientId && !experianRequested
                        ? {
                            value: lang.creditors.requestClientIdExperian,
                            onClick: async () => {
                                setExperianRequested(true);
                                setExperianRequestLoading(true);
                                try {
                                    await creditorsAPI.experianAskForClientId(creditor.creditorId);
                                    alert(lang.creditors.requestClientIdExperianSuccess);
                                }
                                catch (error) {
                                    console.log(error);
                                    if (error.message != null) {
                                        alert(error.message);
                                    }
                                    else {
                                        alert('Error. Try again later.');
                                    }
                                }
                                setExperianRequestLoading(false);
                            },
                            loading: experianRequestLoading,
                        }
                        : undefined, placeholder: lang.creditors.experianClientId, value: experianClientId || '', onChange: (e) => setExperianClientId(e.target.value) }),
            React.createElement(Checkbox, { placeholder: lang.creditors.aksEnabled, checked: aksEnabled, onChange: async (e) => {
                    setAksEnabled(e.target.checked);
                    await creditorsAPI.update(creditor.creditorId, {
                        aksEnabled: e.target.checked,
                    });
                    const blameLog = {
                        details: { toggled: e.target.checked },
                        refId: creditor.creditorId,
                        refType: BlameLogRefType.Creditor,
                        type: BlameLogType.AKSActivated,
                    };
                    tenant && (await blameLogs.createBlameLog(blameLog, tenant.id));
                }, setting: true, size: Size.Small }),
            tenant && meRole && isAdmin(meRole) && (React.createElement(Well, null,
                React.createElement(Header, { size: '3', style: { marginTop: 0 } }, lang.creditors.autoRKI),
                React.createElement(OverrideSetting, { infoIcon: true, iconTip: lang.tenants.autoDebtRegisterInfoTip, id: 'auto-debt-register', checked: state.overrideAutoDebtRegister, placeholder: lang.creditors.automaticDebtRegister, style: { width: '100%' }, onChange: async (e) => {
                        try {
                            await updateOverrideAutoDebtRegister(creditor, e.target.checked, state.autoDebtRegister, state.autoRKIOpen, state.autoRKIIgnoreUnresolvedOppositions);
                            const blameLog = {
                                details: { toggled: e.target.checked },
                                refId: creditor.creditorId,
                                refType: BlameLogRefType.Creditor,
                                type: BlameLogType.RKIOveride,
                            };
                            tenant &&
                                (await blameLogs.createBlameLog(blameLog, tenant.id));
                        }
                        catch (error) {
                            alert('error');
                        }
                    }, iconOnClick: () => {
                        setAutoDebtRegisterModelOpen(!autoDebtRegisterModelOpen);
                    } },
                    React.createElement(Checkbox, { disabled: !state.overrideAutoDebtRegister, style: {
                            marginLeft: '2em',
                        }, size: Size.Small, setting: true, placeholder: lang.creditors.useAutomaticDebtRegister, checked: state.autoDebtRegister, onChange: async (e) => {
                            try {
                                await updateAutoDebtRegister(creditor, e.target.checked);
                                const blameLog = {
                                    details: { toggled: e.target.checked },
                                    refId: creditor.creditorId,
                                    refType: BlameLogRefType.Creditor,
                                    type: BlameLogType.RKIAuto,
                                };
                                tenant &&
                                    (await blameLogs.createBlameLog(blameLog, tenant.id));
                            }
                            catch (error) {
                                alert('error');
                            }
                        } }),
                    React.createElement(Checkbox, { disabled: !state.overrideAutoDebtRegister, style: {
                            marginLeft: '2em',
                        }, size: Size.Small, setting: true, placeholder: lang.creditors.useAutoRKIOpen, checked: state.autoRKIOpen, onChange: async (e) => {
                            try {
                                await updateAutoRKIOpen(creditor, e.target.checked);
                                const blameLog = {
                                    details: { toggled: e.target.checked },
                                    refId: creditor.creditorId,
                                    refType: BlameLogRefType.Creditor,
                                    type: BlameLogType.RKIOpen,
                                };
                                tenant &&
                                    (await blameLogs.createBlameLog(blameLog, tenant.id));
                            }
                            catch (error) {
                                alert('error');
                            }
                        } }),
                    React.createElement(Checkbox, { disabled: !state.overrideAutoDebtRegister, style: {
                            marginLeft: '2em',
                        }, size: Size.Small, setting: true, placeholder: lang.creditors.useAutoRKIIgnoreUnresolvedOppositions, checked: state.autoRKIIgnoreUnresolvedOppositions, onChange: async (e) => {
                            try {
                                await updateAutoRKIIgnoreUnresolvedOppositions(creditor, e.target.checked);
                                const blameLog = {
                                    details: { toggled: e.target.checked },
                                    refId: creditor.creditorId,
                                    refType: BlameLogRefType.Creditor,
                                    type: BlameLogType.RKIIgnoreOppositions,
                                };
                                tenant &&
                                    (await blameLogs.createBlameLog(blameLog, tenant.id));
                            }
                            catch (error) {
                                alert('error');
                            }
                        } })))),
            autoDebtRegisterModelOpen && (React.createElement(Modal, { header: lang.tenants.automaticDebtRegister, onToggle: () => setAutoDebtRegisterModelOpen(!autoDebtRegisterModelOpen), maxWidth: "650px" },
                React.createElement(Well, null,
                    React.createElement("img", { height: "50px", src: rkiLogo }),
                    React.createElement("p", null, lang.tenants.autoDebtRegisterRKI.headline),
                    React.createElement("p", null, lang.tenants.autoDebtRegisterRKI.info),
                    React.createElement("ul", null,
                        React.createElement("li", null, lang.RKI.infobox.min3messages),
                        React.createElement("li", null, lang.RKI.infobox.debtorWarning),
                        React.createElement("li", null, lang.RKI.infobox.closedRegister),
                        React.createElement("ul", null,
                            React.createElement("li", null, lang.RKI.infobox.totalPrincipal),
                            React.createElement("li", null, lang.RKI.infobox.incoming)),
                        React.createElement("li", null, lang.RKI.infobox.openRegister),
                        React.createElement("ul", null,
                            React.createElement("li", null, lang.RKI.infobox.totalDebt),
                            React.createElement("li", null, lang.RKI.infobox.hasFoundation),
                            React.createElement("li", null, lang.RKI.infobox.foundationType))),
                    React.createElement("p", null, lang.tenants.autoDebtRegisterRKI.exclude),
                    React.createElement("ul", null,
                        React.createElement("li", null, lang.RKI.infobox.min18years),
                        React.createElement("li", null, lang.RKI.infobox.specialCreditors)),
                    React.createElement("p", null, lang.tenants.autoDebtRegisterRKI.endingInfo))))))) : (React.createElement(PlaceholderLoader, null))));
};
export default CreditorExperianConfig;
//# sourceMappingURL=CreditorExperianConfig.js.map